import { Component, Input } from '@angular/core';
import { CommonModule, DecimalPipe, AsyncPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { Observable } from 'rxjs';

export interface QuantityUMPCellRendererParams {
  UMPActive$: Observable<boolean>;
}

/**
 * Quantity UMP Cell Renderer
 *
 * Displays quantity information with:
 * - Base quantity (UMB) with unit
 * - Parallel quantity (UMP) with unit (if UMPActive$ observable emits true)
 *
 * Uses async pipe for reactive UMPActive updates.
 *
 * @example
 * ```typescript
 * {
 *   colId: 'quantity',
 *   headerName: 'Cantidad',
 *   cellRenderer: QuantityUMPCellRendererComponent,
 *   cellRendererParams: {
 *     UMPActive$: this.UMPActive$
 *   } as QuantityUMPCellRendererParams,
 *   minWidth: 200
 * }
 * ```
 */
@Component({
  selector: 'app-quantity-ump-cell-renderer',
  standalone: true,
  imports: [CommonModule, AsyncPipe, DecimalPipe],
  template: `
    <div class="table-text-m-2 text-sm">
      <div class="mb-1">
        <b>UMB:</b> {{ params?.data?.baseQuantity | number: '1.3' }}
        <span class="text-gray-500">
          ({{ params?.data?.baseUnitOfMeasure?.name || '-' }})
        </span>
      </div>

      <div *ngIf="(customParams?.UMPActive$ | async)">
        <b>UMP:</b>
        <ng-container *ngIf="params?.data?.pararellUnitOfMeasure; else noUMP">
          {{ params?.data?.pararellQuantity | number: '1.3' }}
          <span class="text-gray-500">
            ({{ params?.data?.pararellUnitOfMeasure?.name }})
          </span>
        </ng-container>
        <ng-template #noUMP>
          <span class="text-gray-500">-</span>
        </ng-template>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `]
})
export class QuantityUMPCellRendererComponent implements CellRenderer<any> {
  @Input() params!: CellRendererParams<any>;

  get customParams(): QuantityUMPCellRendererParams | undefined {
    return this.params?.colDef?.cellRendererParams as QuantityUMPCellRendererParams;
  }
}
